import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import FontAwesomeIcon from 'pf9-ui-components/built/components/FontAwesomeIcon'
import SimpleLink from 'pf9-ui-components/built/components/SimpleLink'
import Card from 'pf9-ui-components/built/elements/card/Card'
import CardBody from 'pf9-ui-components/built/elements/card/CardBody'
import CardHeader from 'pf9-ui-components/built/elements/card/CardHeader'
import React from 'react'
import useReactRouter from 'use-react-router'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'

const Alert = ({ severity, title, description, onClick }) => {
  const classes = useStyles({})

  return (
    <div className={classes.alert} onClick={onClick}>
      <div className={classes.alertTitle}>
        <div className={classes.alertIcon}>
          <FontAwesomeIcon
            className={clsx({
              [classes.statusIcon]: true,
              [classes.warning]: severity === 'Warning',
              [classes.error]: severity === 'Critical',
              [classes.info]: severity === 'Info',
            })}
          >
            exclamation-circle
          </FontAwesomeIcon>
        </div>
        <div className={classes.alertText}>
          <Text variant="caption1">{title}</Text>
        </div>
      </div>
      <Text variant="body2">{description}</Text>
    </div>
  )
}

export default function AlertsCard({ alerts = [], region, onAlertClick, className = undefined }) {
  const classes = useStyles({})
  const { history } = useReactRouter()

  return (
    <Card className={clsx(classes.card, className)} withCustomBody>
      <CardHeader className={classes.alertsCardHeader}>
        <Text variant="subtitle1">Alerts</Text>
      </CardHeader>
      <CardBody className={classes.alertsCardBody}>
        {alerts.length > 0 ? (
          <>
            <div className={classes.alerts}>
              {alerts.map((alert) => (
                <Alert
                  key={alert.id}
                  severity={alert.severity}
                  title={alert.title}
                  description={alert.description}
                  onClick={() => onAlertClick(alert)}
                />
              ))}
            </div>
            <SimpleLink
              variant="primary"
              className={classes.alertsLink}
              textVariant="caption1"
              onClick={() =>
                history.push(routeManager.routes.opencloud.alerts.path({ region: region }))
              }
            >
              {`See all Alerts (${alerts.length}) `}
              <FontAwesomeIcon solid className={classes.arrow}>
                chevron-right
              </FontAwesomeIcon>
            </SimpleLink>
          </>
        ) : (
          <Text variant="body1" className={classes.noAlerts}>
            There are currently no alerts
          </Text>
        )}
      </CardBody>
    </Card>
  )
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  card: {},
  alertsCardBody: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  alertsCardHeader: {
    borderBottom: 'none',
  },
  alerts: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  alert: {
    display: 'grid',
    gridTemplateColumns: 'max-content',
    gridGap: theme.spacing(1),
    background: theme.palette.grey[50],
    padding: theme.spacing(1),
    borderRadius: 4,
    '&:hover': {
      background: theme.palette.grey[100],
      cursor: 'pointer',
    },
  },
  alertTitle: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: 8,
    alignItems: 'center',
  },
  warning: {
    color: theme.components.badge.warning.color,
  },
  error: {
    color: theme.components.badge.error.color,
  },
  info: {
    color: theme.components.badge.secondary.color,
  },
  seeAll: {
    cursor: 'pointer',
  },
  arrow: {
    fontSize: 12,
    color: theme.palette.primary.main,
  },
  alertsLink: {
    display: 'grid',
    justifyContent: 'center',
    padding: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  noAlerts: {
    justifySelf: 'center',
    alignSelf: 'center',
  },
}))
